import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';

import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { DatePickerProps, DatePicker as OrbDatePicker } from '@yandex-360/orb-components/dist/components/DatePicker';

import { useActions } from '../../../../../../shared/hooks';
import { getDatesBounds } from '../../../../helpers/get-dates-bounds';
import { parseDateIfValid } from '../../../../helpers/parse-date-if-valid';
import { setFilterFromTo } from '../../slice';

type FunctionComponentProps = DatePickerProps & {
    bounds: ('from' | 'to')[];
    defaultDate: Date;
};

export const DatePicker: FunctionComponent<FunctionComponentProps> = ({ bounds, defaultDate, ...props }) => {
    const datesBounds = useMemo(getDatesBounds, []);
    const [value, setValue] = useState<string>(dateToString(defaultDate, 'DD.MM.YYYY'));
    const actions = useActions({ setFilterFromTo });
    const preparePayload = useCallback(
        (date: Date) => {
            const payload: ReturnType<typeof setFilterFromTo>['payload'] = {};
            if (bounds.includes('from')) {
                payload.from = dateToString(date, 'YYYY-MM-DD');
            }
            if (bounds.includes('to')) {
                payload.to = dateToString(date, 'YYYY-MM-DD');
            }
            return payload;
        },
        [bounds]
    );
    const onChange = useCallback(
        (value: string) => {
            const parsedDate = parseDateIfValid(value);
            if (parsedDate) {
                actions.setFilterFromTo(preparePayload(parsedDate));
            }
            setValue(value);
        },
        [actions, preparePayload]
    );
    const onBlur = useCallback(() => {
        if (!parseDateIfValid(value)) {
            setValue(dateToString(defaultDate, 'DD.MM.YYYY'));
            actions.setFilterFromTo(preparePayload(defaultDate));
        }
    }, [actions, defaultDate, preparePayload, value]);
    return (
        <OrbDatePicker
            onChange={onChange}
            value={value}
            onBlur={onBlur}
            minValue={datesBounds.from}
            maxValue={datesBounds.to}
            hasClear={false}
            {...props}
        />
    );
};
